import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { mergeProps } from '@react-aria/utils'
import type { FocusableElement } from '@react-types/shared'
import {
  cloneElement,
  isValidElement,
  useCallback,
  useMemo,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react'

import { useDOMRef } from '@/src/general/utils/useDOMref'

import type { TDefaultButtonCommonVariantProps } from './default/variants/default-button-variants-common'
import defaultButtonWebVariants from './default/variants/default-button-variants-web'
import { useAriaButton, type TAriaButtonProps } from './useAriaButton'
import type { TProps as TNativeButtonProps } from './useButton.type'

type TProps = HTMLUiProps<'button'> & Omit<TNativeButtonProps, 'ref'>

export type TUseButtonProps = TProps &
  Omit<TAriaButtonProps, keyof TDefaultButtonCommonVariantProps> &
  Omit<TDefaultButtonCommonVariantProps, 'isInGroup'> & {
    ref?: React.Ref<HTMLDivElement>
  }

const getIconClone = (icon: ReactNode) =>
  isValidElement(icon)
    ? cloneElement(icon, {
        // @ts-expect-error : not documented
        'aria-hidden': true,
        focusable: false,
        tabIndex: -1,
      })
    : null

export function useButton(props: TUseButtonProps) {
  const {
    ref,
    as: _as,
    disableRipple: _disableRipple,
    children,
    startContent: startContentProp,
    endContent: endContentProp,
    autoFocus,
    className,
    spinner,
    isInactive,
    isLoading = false,
    fullWidth = false,
    radius,
    size = 'md',
    colors = 'default',
    variant = 'solid',
    isDisabled: isDisabledProp = false,
    isIconOnly = false,
    spinnerPlacement = 'start',
    onPress,
    ...otherProps
  } = props

  const domRef = useDOMRef(ref)

  const { isFocusVisible, isFocused, focusProps } = useFocusRing({
    autoFocus,
  })

  const isDisabled = isDisabledProp || isLoading

  const styles = useMemo(
    () =>
      defaultButtonWebVariants({
        size,
        colors,
        variant,
        radius,
        fullWidth,
        isDisabled: isDisabled || isInactive,
        isIconOnly,
        className,
      }),
    [size, colors, variant, radius, fullWidth, isDisabled, isInactive, isIconOnly, className],
  )

  const {
    // buttonProps: ariaButtonProps,
    isPressed,
  } = useAriaButton(
    {
      isDisabled,
      onPress,
      ...otherProps,
    } as TAriaButtonProps,
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- library interop
    domRef as RefObject<FocusableElement>,
  )

  const { isHovered, hoverProps } = useHover({ isDisabled })

  const getButtonProps: PropGetter<ButtonHTMLAttributes<HTMLDivElement> & { disableRipple?: boolean }, HTMLDivElement> =
    useCallback(
      () => ({
        'data-disabled': isDisabled,
        'data-focus': isFocused,
        'data-pressed': isPressed,
        'data-focus-visible': isFocusVisible,
        'data-hover': isHovered,
        'data-loading': isLoading,
        ...mergeProps<ButtonHTMLAttributes<HTMLDivElement>[]>(
          // ariaButtonProps,
          focusProps,
          hoverProps,
          otherProps,
        ),
      }),
      [
        isLoading,
        isDisabled,
        isFocused,
        isPressed,
        isFocusVisible,
        isHovered,
        // ariaButtonProps,
        focusProps,
        hoverProps,
        otherProps,
      ],
    )

  const startContent = getIconClone(startContentProp)
  const endContent = getIconClone(endContentProp)

  return {
    children,
    domRef,
    spinner,
    styles,
    startContent,
    endContent,
    isLoading,
    spinnerPlacement,
    getButtonProps,
    isIconOnly,
  }
}
